import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VerificationHistory = ({ history, onViewDetails, onExportHistory }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return { name: 'CheckCircle', color: 'text-success' };
      case 'invalid':
        return { name: 'XCircle', color: 'text-error' };
      case 'expired':
        return { name: 'Clock', color: 'text-warning' };
      case 'revoked':
        return { name: 'Ban', color: 'text-error' };
      default:
        return { name: 'AlertCircle', color: 'text-muted-foreground' };
    }
  };

  const getCredentialTypeIcon = (type) => {
    switch (type) {
      case 'vaccination_proof':
        return 'Shield';
      case 'insurance_coverage':
        return 'Heart';
      case 'training_certificate':
        return 'GraduationCap';
      case 'prescription_auth':
        return 'Pill';
      default:
        return 'FileText';
    }
  };

  const filteredHistory = history?.filter(item => {
      const matchesSearch = item?.credentialName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.subjectName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.issuerName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesFilter = filterStatus === 'all' || item?.status === filterStatus;
      return matchesSearch && matchesFilter;
    })?.sort((a, b) => {
      if (sortBy === 'timestamp') {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } else if (sortBy === 'status') {
        return a?.status?.localeCompare(b?.status);
      } else if (sortBy === 'type') {
        return a?.credentialType?.localeCompare(b?.credentialType);
      }
      return 0;
    });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'valid':
        return `${baseClasses} bg-success/10 text-success`;
      case 'invalid':
        return `${baseClasses} bg-error/10 text-error`;
      case 'expired':
        return `${baseClasses} bg-warning/10 text-warning`;
      case 'revoked':
        return `${baseClasses} bg-error/10 text-error`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Verification History</h2>
          <p className="text-muted-foreground">Recent credential verification results</p>
        </div>
        <Button variant="outline" size="sm" onClick={onExportHistory}>
          <Icon name="Download" size={16} className="mr-2" />
          Export
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Input
          type="search"
          placeholder="Search verifications..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
        />
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Status</option>
          <option value="valid">Valid</option>
          <option value="invalid">Invalid</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="timestamp">Sort by Date</option>
          <option value="status">Sort by Status</option>
          <option value="type">Sort by Type</option>
        </select>
      </div>
      {/* History List */}
      <div className="space-y-3">
        {filteredHistory?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No verification records found</p>
          </div>
        ) : (
          filteredHistory?.map((item) => {
            const statusIcon = getStatusIcon(item?.status);
            const typeIcon = getCredentialTypeIcon(item?.credentialType);
            
            return (
              <div
                key={item?.id}
                className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-clinical cursor-pointer"
                onClick={() => onViewDetails(item)}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Icon name={typeIcon} size={20} className="text-primary" />
                    <Icon name={statusIcon?.name} size={16} className={statusIcon?.color} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="font-medium text-foreground">{item?.credentialName}</h3>
                      <span className={getStatusBadge(item?.status)}>
                        {item?.status?.charAt(0)?.toUpperCase() + item?.status?.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span>{item?.subjectName}</span>
                      <span>•</span>
                      <span>{item?.issuerName}</span>
                      <span>•</span>
                      <span>{formatDate(item?.timestamp)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {item?.zkProofs && item?.zkProofs?.length > 0 && (
                    <div className="flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-full">
                      <Icon name="Shield" size={12} className="text-primary" />
                      <span className="text-xs text-primary">ZK</span>
                    </div>
                  )}
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Summary Stats */}
      {history?.length > 0 && (
        <div className="border-t border-border pt-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">
                {history?.length}
              </p>
              <p className="text-sm text-muted-foreground">Total Scans</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-success">
                {history?.filter(h => h?.status === 'valid')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Valid</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-error">
                {history?.filter(h => h?.status === 'invalid' || h?.status === 'revoked')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Invalid</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-warning">
                {history?.filter(h => h?.status === 'expired')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Expired</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationHistory;